// frontend/src/features/ups/OrganizedDriverList.js
// Renders UPS Active (“Organized”) drivers using Phase 5 assignments read model,
// while keeping existing actions (details/reserve/ban) intact.

// Phase 09 note:
// - Active is operationally time-sensitive, but the current backend time_bucket
//   values (today/scheduled/historical) do not fully express the intended UX:
//   "today if there is a current shift, otherwise next upcoming operational shift".
// - Therefore this tab remains broad for now (no time bucket), while preserving
//   canonical starts_at / ends_at on rows for future refinement.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye, Ban as BannedIcon } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';

// site selector
import { selectConcreteSelectedSiteId, selectIsAllSitesMode, } from '../sites/sitesSlice';

// Phase 5: assignments-based read model
import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';

import { upsertDriver } from '../drivers/driverSlice';

import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import DriverDetailsModal from './modals/DriverDetailsModal';
import { reserveDriver, banDriver } from '../../services/upsDriverService';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverMobileCard from './components/DriverMobileCard';

const ACTIVE_ASSIGNMENT_QUERY = {
  statuses: ['active'],
  timeBucket: undefined,
};

export default function OrganizedDriverList() {
  const dispatch = useDispatch();
  const banReasonId = 'organized-driver-ban-reason';
  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, source } = useSelector(selectFilters);

  const selectedSiteId = useSelector(selectConcreteSelectedSiteId);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);

  // Phase 5: select assignment rows for the Active/Organized tab
  const selectActiveAssignments = useMemo(() => makeSelectAssignmentsForTab('active'), []);
  const rows = useSelector(selectActiveAssignments);

  // ✅ RENAMED: drivers -> activeRows
  // Represents composite assignment-driver DTOs
  const activeRows = useMemo(
    () =>
      (rows || [])
        .filter((r) => !r?.is_banned)
        .map((r) => ({
          ...r,

          // Phase 09 canonical time carry-through
          starts_at: r.starts_at ?? r.start_time ?? null,
          ends_at: r.ends_at ?? r.end_time ?? null,

          // Keep legacy time fields during dual-read transition
          start_time: r.start_time ?? null,
          end_time: r.end_time ?? null,
        })),
    [rows]
);

useEffect(() => {
  if (!isAllSitesMode && !selectedSiteId) return;
  dispatch(loadSiteAssignments({ ...ACTIVE_ASSIGNMENT_QUERY }));
}, [dispatch, agencyId, q, source, selectedSiteId, isAllSitesMode]);

  // Focus-on-arrival via ?focus=<id> (driver id)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  const reloadActiveAssignments = () => {
  dispatch(loadSiteAssignments({ ...ACTIVE_ASSIGNMENT_QUERY }));
  };

  // Reserve: reserve then reload assignments for current site
  const handleReserve = async (row) => {
    const actionSiteId = row?.site_id || selectedSiteId;
    if (!row?.id || !actionSiteId) return;

    try {
      await reserveDriver(row.id, actionSiteId);
      reloadActiveAssignments();
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Reserve failed:', err);
    }
  };

  // Ban modal
  const [showBanModal, setShowBanModal] = useState(false);
  // ✅ RENAMED: selectedDriver -> selectedRow
  const [selectedRow, setSelectedRow] = useState(null);
  const [banReason, setBanReason] = useState('');

  const openBanModal = (row) => {
    if (!isAdmin) return;

    dispatch(upsertDriver(row)); // ✅ keeps driverSlice consistent for anything modal-related

    setSelectedRow(row);
    setBanReason('');
    setShowBanModal(true);
  };

  const handleBanConfirm = async () => {
    if (!isAllSitesMode && !selectedSiteId) return;
    if (!banReason.trim() || !isAdmin) return;

    try {
      if (!selectedRow?.id) return;

      const reason = banReason.trim();
      if (!reason) return;

      await banDriver(selectedRow.id, reason);

      reloadActiveAssignments();
      dispatch(bumpStatsRefreshKey());
      
      setShowBanModal(false);
      setSelectedRow(null);
    } catch (err) {
      console.error('Ban failed:', err);
    }
  };

  // Driver details modal
  const [showDetails, setShowDetails] = useState(false);
  // ✅ RENAMED: detailsDriver -> detailsRow
  const [detailsRow, setDetailsRow] = useState(null);
  const openDetails = (row) => {
    dispatch(upsertDriver(row)); // ✅ prime redux so modal is live immediately
    setDetailsRow(row);
    setShowDetails(true);
  };
  const closeDetails = () => {
    setShowDetails(false);
    setDetailsRow(null);
  };

// Empty-state filter context labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();
  const hasSearch = (q || '').trim().length > 0;

  return (
      <>
        <section className="my-6 overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        {/* Card heading */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-app-text">Driver Queue</h3>
            <p className="mt-1 text-sm text-app-muted">
              Clean triage for active drivers, dispatch actions, and compliance review.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {activeRows.length} active
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['Driver', 'Vehicle', 'Phone', 'Email', 'Owner', 'Site', 'Submitted', 'Actions'].map(
                  (col) => (
                    <th
                      key={col}
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
                    >
                      {col}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {activeRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-app-muted">
                    No active drivers{agencyId ? ` for ${scopeLabel}` : ''}
                    {hasSearch ? ` matching “${q}”` : ''}.
                  </td>
                </tr>
              ) : (
                activeRows.map((row) => (
                  <tr
                    key={row.assignment_id ?? row.id}
                    data-row-id={row.id}
                    className="transition hover:bg-app-surface"
                  >
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-app-accent bg-app-primary text-xs font-bold uppercase text-app-accent">
                          {(row.name || '—')
                            .split(' ')
                            .map((part) => part[0])
                            .join('')
                            .slice(0, 2)}
                        </div>

                        <div>
                          <div className="font-semibold text-app-text">{row.name || '—'}</div>
                          <div className="text-xs text-app-muted">
                            Driver ID · {row.id ? `DRV-${row.id}` : '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.vehicle_registration_number || row.vrn || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.phone || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-muted">
                      {row.email || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {formatOwnerLabel(row)}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.site_name || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.submission_date ? new Date(row.submission_date).toLocaleString() : '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetails(row)}
                          aria-label="View driver details"
                          title="View details"
                          className="rounded-xl border border-app-border bg-app-card px-3 py-2 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleReserve(row)}
                          disabled={!!row.is_banned}
                          aria-label="Reserve driver"
                          title={row.is_banned ? 'Driver is banned' : 'Reserve'}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (row.is_banned
                              ? 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                              : 'border-app-border bg-app-card text-app-accent hover:bg-app-surface')
                          }
                        >
                          <UserCheck size={16} />
                        </button>

                        <button
                          onClick={() => openBanModal(row)}
                          aria-label="Ban driver"
                          title={isAdmin ? 'Ban' : 'Admins only'}
                          disabled={!isAdmin}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (isAdmin
                              ? 'border-red-200 bg-app-card text-red-600 hover:bg-red-50'
                              : 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50')
                          }
                        >
                          <BannedIcon size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {activeRows.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              No active drivers{agencyId ? ` for ${scopeLabel}` : ''}
              {hasSearch ? ` matching "${q}"` : ''}.
            </div>
          ) : (
            activeRows.map((row) => {
              const avatarText = (row.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <div key={row.assignment_id ?? row.id} data-row-id={row.id}>
                  <DriverMobileCard
                    title={row.name || '—'}
                    subtitle={`Driver ID · ${row.id ? `DRV-${row.id}` : '—'}`}
                    avatarText={avatarText}
                    fields={[
                      ['Vehicle', row.vehicle_registration_number || row.vrn],
                      ['Owner', formatOwnerLabel(row)],
                      ['Site', row.site_name],
                      ['Phone', row.phone],
                      ['Email', row.email],
                      ['Submitted', row.submission_date ? new Date(row.submission_date).toLocaleString() : null],
                      ['Assignment Status', row.assignment_status],
                    ]}

                    actions={
                      <>
                        <button
                          onClick={() => openDetails(row)}
                          aria-label="View driver details"
                          title="View details"
                          className="rounded-xl border border-app-border bg-app-card px-3 py-2 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleReserve(row)}
                          disabled={!!row.is_banned}
                          aria-label="Reserve driver"
                          title={row.is_banned ? 'Driver is banned' : 'Reserve'}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (row.is_banned
                              ? 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                              : 'border-app-border bg-app-card text-app-accent hover:bg-app-surface')
                          }
                        >
                          <UserCheck size={16} />
                        </button>

                        <button
                          onClick={() => openBanModal(row)}
                          aria-label="Ban driver"
                          title={isAdmin ? 'Ban' : 'Admins only'}
                          disabled={!isAdmin}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (isAdmin
                              ? 'border-red-200 bg-app-card text-red-600 hover:bg-red-50'
                              : 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50')
                          }
                        >
                          <BannedIcon size={16} />
                        </button>
                      </>
                    }
                  />
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Ban Modal */}
      {showBanModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowBanModal(false)}
        >
          <div
            className="flex h-[70dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-md sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="organized-ban-title"
          >
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
              <h2 id="organized-ban-title" className="text-xl font-semibold text-app-text">
                Ban {selectedRow?.name}
              </h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="rounded-xl px-3 py-2 text-xl font-bold text-app-muted transition hover:bg-app-surface hover:text-app-text"
                aria-label="Close ban modal"
              >
                &times;
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
              <label htmlFor={banReasonId} className="mb-2 block text-sm font-medium text-app-muted">
                Ban reason
              </label>
              <textarea
                id={banReasonId}
                name="banReason"
                rows={4}
                placeholder="Enter ban reason"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20"
              />
            </div>

            <div
              className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
            >
              <button
                onClick={() => setShowBanModal(false)}
                className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface"
              >
                Cancel
              </button>
              <button
                onClick={handleBanConfirm}
                disabled={!isAdmin}
                title={isAdmin ? 'Confirm Ban' : 'Admins only'}
                className={
                  'rounded-xl px-5 py-2.5 text-sm font-bold shadow-sm transition ' +
                  (isAdmin
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'cursor-not-allowed bg-app-surface text-app-muted/50')
                }
              >
                Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal */}
      {showDetails && detailsRow && (
        <DriverDetailsModal driver={detailsRow} onClose={closeDetails} />
      )}
    </>
  );
}
